import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffApi, rolesApi } from '../api/staffApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreateStaffPayload, UpdateStaffPayload } from '../types';

const errMsg = (err: unknown, fallback: string) => ((err as Error)?.message) || fallback;

export const useStaff = () =>
  useQuery({ queryKey: ['tcs-staff'], queryFn: staffApi.list, staleTime: 2 * 60 * 1000 });

export const useRoles = () =>
  useQuery({ queryKey: ['roles'], queryFn: rolesApi.list, staleTime: 10 * 60 * 1000 });

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateStaffPayload) => staffApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tcs-staff'] });
      toast.success('Staff member created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create staff member')),
  });
};

export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStaffPayload }) => staffApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tcs-staff'] });
      toast.success('Staff member updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update staff member')),
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => staffApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tcs-staff'] });
      toast.success('Staff member removed');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to remove staff member')),
  });
};
